import { Saga } from 'redux-saga'
import { call, cancelled } from 'redux-saga/effects'
import { v4 as uuidv4 } from 'uuid'

type Awaiter = () => void

type LockState = {
  locked?: boolean
  cookie?: string
  awaiters: Awaiter[]
}

const locks: Map<string, LockState> = new Map()
const lockKeyGenerator = () => uuidv4()

function lock(key: string, { signal }: { signal?: AbortSignal } = {}): Promise<string> {
  return new Promise<string>((resolve) => {
    const cookie = lockKeyGenerator()
    let lockState: LockState = locks.get(key) as LockState
    if (!lockState) {
      lockState = { locked: false, awaiters: [] }
      locks.set(key, lockState)
    }

    const attemptLock = () => {
      if (lockState.locked) {
        lockState.awaiters.push(attemptLock)
      } else {
        lockState.locked = true
        lockState.cookie = cookie
        resolve(cookie)
      }
    }
    signal?.addEventListener('abort', () => {
      if (lockState.cookie === cookie) {
        unlock(key, cookie)
      } else {
        lockState.awaiters = lockState.awaiters.filter((awaiter) => awaiter !== attemptLock)
      }
    })
    attemptLock()
  })
}

function unlock(key: string, cookie: string) {
  const lockState = locks.get(key)
  if (!lockState) {
    throw new Error(`unlock for ${key} with no lock`)
  }
  if (lockState.cookie !== cookie) {
    throw new Error(`unlock for ${key} with invalid cookie`)
  }
  lockState.locked = false
  lockState.cookie = undefined
  const nextAwaiter = lockState.awaiters.shift()
  if (nextAwaiter) {
    setTimeout(nextAwaiter, 0)
  }
}

export function* runTransactionSaga(key: string, saga: unknown, ...args: unknown[]) {
  let lockCookie = ''
  const abortController = new AbortController()
  try {
    lockCookie = yield lock(key, { signal: abortController.signal })

    yield call(saga as Saga, ...args)
  } finally {
    if (yield cancelled()) {
      abortController.abort()
    } else if (lockCookie) {
      unlock(key, lockCookie)
    }
  }
}

export async function runTransactionAsync<T>(key: string, fun: () => Promise<T>) {
  let lockCookie = ''
  try {
    lockCookie = await lock(key)

    return await fun()
  } finally {
    if (lockCookie) {
      unlock(key, lockCookie)
    }
  }
}
